import { Link } from "react-router-dom";

const GITHUB_URL = "https://github.com/ParkerCai/Trace-BIM";
const REPORT_URL = "/files/Trace-BIM-Case-Study.pdf";

const workflow = [
  {
    number: "01",
    title: "Normalize + scale",
    text: "Correct rotation only when the drawing supports it, then fit a pixel-to-model transform from known dimensions. Scale residuals stay with the package.",
    output: "Shared model frame",
  },
  {
    number: "02",
    title: "Build semantic masks",
    text: "Separate wall poche, floors, openings, railings, fixtures, and annotation. Vision models propose masks; the source plan remains the authority.",
    output: "Verified object masks",
  },
  {
    number: "03",
    title: "Reconstruct geometry",
    text: "Use OpenCV and deterministic geometry operations to pair wall faces, resolve junctions, retain short walls, and close gaps after opening records are saved.",
    output: "Topological vectors",
  },
  {
    number: "04",
    title: "Assign BIM semantics",
    text: "Add wall types and location rules, opening hosts, door hand and swing, family axes, fixture dimensions, and placement behavior.",
    output: "Revit-aware records",
  },
  {
    number: "05",
    title: "Export one package",
    text: "Write semantic JSON first. Derive SVG and DXF from the same coordinates and layer IDs, with no hidden transforms between formats.",
    output: "JSON + SVG + DXF",
  },
  {
    number: "06",
    title: "Validate + review",
    text: "Check units, extents, layer parity, hosts, directions, collisions, and warnings. A QA overlay makes uncertain geometry visible before Revit placement.",
    output: "QA overlay + report",
  },
];

const modelingRules = [
  {
    title: "Control the right wall face",
    text: "Perimeter walls follow the exterior finish face. Interior partitions use paired-face centerlines unless a project rule says otherwise.",
  },
  {
    title: "Record openings before repair",
    text: "Door and window data is saved before wall gaps are closed, so Revit receives continuous host walls and explicit opening records.",
  },
  {
    title: "Model the family behavior",
    text: "Line-based, level-based, wall-hosted, face-hosted, and path-based families each receive the placement data they actually need.",
  },
  {
    title: "Test the complete footprint",
    text: "Collision checks use the family body, not just an insertion point or reference line. Adjacent fixtures are allocated together.",
  },
];

const results = [
  { value: "2", label: "independent agent runs" },
  { value: "0", label: "manual geometry edits" },
  { value: "3", label: "coordinated exchange formats" },
  { value: "1", label: "canonical source of truth" },
];

const limitations = [
  "A raster plan cannot supply reliable heights. Plan-only values must come from an approved family catalog or remain flagged as assumptions.",
  "Scale recovery depends on trustworthy dimensions or surveyed anchors. Nominal wall thickness is not enough.",
  "Revit family origins and parameter axes vary by library and version, so every new catalog needs a placement check.",
  "This apartment study did not test multi-discipline federation or data-center coordination. The output still requires professional review before documentation.",
];

function ExternalLink({
  href,
  children,
  variant = "solid",
}: {
  href: string;
  children: React.ReactNode;
  variant?: "solid" | "outline";
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={
        variant === "solid"
          ? "inline-flex min-h-11 items-center justify-center rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          : "inline-flex min-h-11 items-center justify-center rounded-full border border-border bg-background/70 px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      }
    >
      {children}
      <span aria-hidden="true" className="ml-2">
        ↗
      </span>
    </a>
  );
}

export default function TraceBim() {
  return (
    <main
      id="trace-bim"
      className="min-h-screen overflow-hidden bg-background text-foreground"
    >
      <header className="relative border-b border-border px-5 pb-12 pt-24 sm:px-8 md:pb-16 md:pt-32 lg:px-12 lg:pt-36">
        <div className="pointer-events-none absolute -right-28 -top-24 h-80 w-80 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="pointer-events-none absolute left-1/3 top-28 h-64 w-64 rounded-full bg-fuchsia-500/10 blur-3xl" />

        <div className="relative mx-auto max-w-7xl">
          <Link
            to="/projects"
            className="mb-8 inline-flex items-center text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span aria-hidden="true" className="mr-2">
              ←
            </span>
            All projects
          </Link>

          <div className="grid items-end gap-10 lg:grid-cols-[minmax(0,0.95fr)_minmax(32rem,1.2fr)] lg:gap-14">
            <div>
              <p className="mb-5 text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                Independent R&amp;D · 2026
              </p>
              <h1 className="max-w-3xl text-6xl font-light tracking-[-0.055em] sm:text-7xl md:text-8xl lg:text-[7rem] lg:leading-[0.88]">
                Trace<span className="text-muted-foreground">-BIM</span>
              </h1>
              <p className="mt-8 max-w-2xl text-2xl font-light leading-snug sm:text-3xl">
                From raster plan to native Revit model.
              </p>
              <p className="mt-5 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
                An experimental workflow I built to turn architectural plan
                images into traceable BIM geometry. Semantic recognition starts
                the process, deterministic geometry keeps it measurable, and
                coding agents place native elements in Revit through MCP.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <ExternalLink href={GITHUB_URL}>View agent skill</ExternalLink>
                <ExternalLink href={REPORT_URL} variant="outline">
                  Read full report
                </ExternalLink>
              </div>
            </div>

            <figure className="overflow-hidden rounded-4xl border border-border bg-muted shadow-2xl shadow-black/10">
              <img
                src="/images/projects/trace-bim/revit-qa-collage.png"
                alt="Revit quality-assurance views showing the generated apartment model, plan, kitchen, bathroom, balcony, and shared coordinates"
                className="aspect-[1.31/1] h-full w-full object-cover"
                fetchPriority="high"
              />
              <figcaption className="border-t border-border px-4 py-3 text-xs leading-5 text-muted-foreground">
                Revit QA set: model, hosted families, room data, balcony detail,
                and shared-coordinate checks.
              </figcaption>
            </figure>
          </div>

          <dl className="mt-12 grid gap-px overflow-hidden rounded-3xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Role", "Design technologist / BIM workflow designer"],
              ["Input", "Raster apartment plan + approved family catalog"],
              ["Output", "JSON, SVG, DXF + native Revit elements"],
              ["Stack", "Codex, Claude Code, Gemini, OpenCV, MCP, Revit"],
            ].map(([term, detail]) => (
              <div key={term} className="bg-background p-5 sm:p-6">
                <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  {term}
                </dt>
                <dd className="mt-2 text-sm leading-6">{detail}</dd>
              </div>
            ))}
          </dl>
        </div>
      </header>

      <section
        aria-labelledby="brief-heading"
        className="px-5 py-20 sm:px-8 md:py-28 lg:px-12"
      >
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-20">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              The brief
            </p>
            <h2
              id="brief-heading"
              className="mt-4 text-4xl font-light tracking-tight sm:text-5xl"
            >
              A line is not yet a BIM element.
            </h2>
          </div>
          <div className="space-y-7 text-lg leading-8 text-muted-foreground">
            <p>
              The study began with one apartment plan and an approved Revit
              family catalog. The drawing had no machine-readable wall types,
              hosts, directions, or local axes. It was useful to a person, but
              incomplete as an automation contract.
            </p>
            <p>
              The difficult part was preserving design intent: which face of a
              wall must stay fixed, which wall hosts an opening, how a family is
              placed, and whether its full body collides with adjacent work.
              Trace-BIM makes those decisions explicit before an agent touches
              the Revit model.
            </p>
            <aside className="rounded-3xl border border-border bg-muted/50 p-6 text-base leading-7 text-foreground">
              <strong className="font-semibold">Design premise:</strong> JSON is
              the source of truth. SVG and DXF are derived views of the same
              coordinates, not separate redraws.
            </aside>
          </div>
        </div>
      </section>

      <section
        aria-labelledby="translation-heading"
        className="border-y border-border bg-muted/30 px-5 py-20 sm:px-8 md:py-28 lg:px-12"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Translation layer
            </p>
            <h2
              id="translation-heading"
              className="mt-4 text-4xl font-light tracking-tight sm:text-5xl"
            >
              Source → mask → semantic geometry
            </h2>
            <p className="mt-5 text-lg leading-8 text-muted-foreground">
              Vision proposes what each pixel means. OpenCV and explicit BIM
              rules decide the geometry that moves downstream.
            </p>
          </div>

          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {[
              {
                src: "/images/projects/trace-bim/source-plan.png",
                alt: "Clean apartment source plan with walls, openings, and fixtures",
                step: "01 / Source",
                caption: "The raster drawing remains the visual authority.",
              },
              {
                src: "/images/projects/trace-bim/wall-mask.png",
                alt: "Black wall-poche mask extracted from the apartment plan",
                step: "02 / Mask",
                caption: "A continuous wall mask supports topology recovery.",
              },
              {
                src: "/images/projects/trace-bim/semantic-wall-geometry.png",
                alt: "Wall geometry overlay with color-coded location lines and thickness classes",
                step: "03 / Geometry",
                caption:
                  "Controlled faces and centerlines carry modeling intent.",
              },
            ].map((item) => (
              <figure
                key={item.step}
                className="overflow-hidden rounded-3xl border border-border bg-background"
              >
                <div className="aspect-[4/3] overflow-hidden bg-white">
                  <img
                    src={item.src}
                    alt={item.alt}
                    loading="lazy"
                    className="h-full w-full object-contain"
                  />
                </div>
                <figcaption className="border-t border-border p-5">
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {item.step}
                  </span>
                  <p className="mt-2 text-sm leading-6">{item.caption}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section
        aria-labelledby="workflow-heading"
        className="px-5 py-20 sm:px-8 md:py-28 lg:px-12"
      >
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 lg:grid-cols-[0.75fr_1.25fr] lg:gap-20">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                Method
              </p>
              <h2
                id="workflow-heading"
                className="mt-4 text-4xl font-light tracking-tight sm:text-5xl"
              >
                Six gated stages
              </h2>
              <p className="mt-5 max-w-md text-lg leading-8 text-muted-foreground">
                Recognition, geometry, and BIM placement are separate checks. A
                defect has to pass its own gate before it can contaminate the
                next stage.
              </p>
            </div>

            <ol className="border-t border-border">
              {workflow.map((step) => (
                <li
                  key={step.number}
                  className="grid gap-4 border-b border-border py-7 sm:grid-cols-[4rem_1fr_auto] sm:items-start"
                >
                  <span className="font-mono text-sm text-muted-foreground">
                    {step.number}
                  </span>
                  <div>
                    <h3 className="text-xl font-semibold">{step.title}</h3>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                      {step.text}
                    </p>
                  </div>
                  <span className="w-fit rounded-full border border-border px-3 py-1 text-xs text-muted-foreground sm:mt-0.5">
                    {step.output}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section
        aria-labelledby="semantics-heading"
        className="border-y border-border bg-neutral-950 px-5 py-20 text-neutral-50 sm:px-8 md:py-28 lg:px-12"
      >
        <div className="mx-auto grid max-w-7xl items-start gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-neutral-400">
              Canonical package
            </p>
            <h2
              id="semantics-heading"
              className="mt-4 text-4xl font-light tracking-tight sm:text-5xl"
            >
              Geometry with a reason attached.
            </h2>
            <p className="mt-5 max-w-xl text-lg leading-8 text-neutral-400">
              Every feature records its source evidence, confidence, host
              relationships, local axes, and placement parameters. The package
              can explain how a wall or family reached Revit.
            </p>
            <ul className="mt-8 grid gap-3 text-sm text-neutral-300 sm:grid-cols-2">
              {[
                "Millimeter model frame",
                "Version-aligned files",
                "Stable semantic layer IDs",
                "Explicit host relations",
                "Raw pixel provenance",
                "Warnings for uncertain objects",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-3 rounded-2xl border border-neutral-800 px-4 py-3"
                >
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 rounded-full bg-cyan-400"
                  />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="overflow-hidden rounded-3xl border border-neutral-800 bg-neutral-900 shadow-2xl shadow-black/30">
            <div className="flex items-center gap-2 border-b border-neutral-800 px-5 py-4">
              <span className="h-2.5 w-2.5 rounded-full bg-fuchsia-400" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
              <span className="h-2.5 w-2.5 rounded-full bg-cyan-400" />
              <span className="ml-2 font-mono text-xs text-neutral-500">
                package.json
              </span>
            </div>
            <pre className="overflow-x-auto p-5 text-xs leading-6 text-neutral-300 sm:p-7 sm:text-sm">
              <code>{`{
  "schema": "raster-plan-bim/v1",
  "frame": {
    "units": "mm",
    "orientation": "y_down",
    "pixel_to_model": {
      "method": "multi-anchor fit",
      "rms_error_mm": 0
    }
  },
  "features": [{
    "id": "W001",
    "kind": "wall",
    "properties": {
      "wall_width_mm": 200,
      "location_rule": "finish_face_exterior",
      "body_side": "infer_from_floor"
    },
    "provenance": {
      "method": "paired wall faces",
      "confidence": 0.96
    }
  }]
}`}</code>
            </pre>
          </div>
        </div>
      </section>

      <section
        aria-labelledby="revit-heading"
        className="px-5 py-20 sm:px-8 md:py-28 lg:px-12"
      >
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                Revit-aware modeling
              </p>
              <h2
                id="revit-heading"
                className="mt-4 text-4xl font-light tracking-tight sm:text-5xl"
              >
                Placement rules are part of the model.
              </h2>
              <p className="mt-5 max-w-xl text-lg leading-8 text-muted-foreground">
                A family catalog defines more than names and dimensions. It
                tells the agent how each object is hosted, oriented, resized,
                and checked.
              </p>
              <div className="mt-9 grid gap-6 sm:grid-cols-2">
                {modelingRules.map((rule) => (
                  <article
                    key={rule.title}
                    className="border-l-2 border-foreground pl-4"
                  >
                    <h3 className="font-semibold">{rule.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      {rule.text}
                    </p>
                  </article>
                ))}
              </div>
            </div>

            <figure className="overflow-hidden rounded-3xl border border-border bg-white">
              <img
                src="/images/projects/trace-bim/family-catalog.png"
                alt="Approved Revit family catalog listing doors, windows, fixtures, walls, floor, railing, and family placement behavior"
                loading="lazy"
                className="h-full w-full object-contain"
              />
              <figcaption className="border-t border-border bg-background px-5 py-4 text-xs leading-5 text-muted-foreground">
                The approved family library was treated as an input contract,
                not a visual reference sheet.
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section
        aria-labelledby="automation-heading"
        className="border-y border-border bg-muted/30 px-5 py-20 sm:px-8 md:py-28 lg:px-12"
      >
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <figure className="overflow-hidden rounded-3xl border border-border bg-background">
            <img
              src="/images/projects/trace-bim/revit-warning.png"
              alt="Revit warning dialog reporting overlapping walls during automated model creation"
              loading="lazy"
              className="aspect-[1.4/1] h-full w-full object-cover object-center"
            />
            <figcaption className="border-t border-border px-5 py-4 text-xs leading-5 text-muted-foreground">
              A Revit overlap warning exposed a geometry rule failure during
              automation.
            </figcaption>
          </figure>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Automation + recovery
            </p>
            <h2
              id="automation-heading"
              className="mt-4 text-4xl font-light tracking-tight sm:text-5xl"
            >
              Fix the rule, then rebuild.
            </h2>
            <p className="mt-5 text-lg leading-8 text-muted-foreground">
              Codex and Claude Code drove Revit through MCP to create walls,
              hosted openings, families, views, schedules, and tear sheets. GUI
              warnings could still interrupt an unattended run.
            </p>
            <p className="mt-5 text-lg leading-8 text-muted-foreground">
              When a repeated defect appeared, I traced it back to the governing
              geometry or placement rule and regenerated the package. The review
              loop did not rely on patching the one location visible in a
              screenshot.
            </p>
          </div>
        </div>
      </section>

      <section
        aria-labelledby="results-heading"
        className="px-5 py-20 sm:px-8 md:py-28 lg:px-12"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Result
            </p>
            <h2
              id="results-heading"
              className="mt-4 text-4xl font-light tracking-tight sm:text-5xl"
            >
              Two complete runs, one reusable method.
            </h2>
            <p className="mt-5 text-lg leading-8 text-muted-foreground">
              Both coding-agent runs produced native Revit models and tear
              sheets without manual geometry edits. The stronger result came
              from richer opening and fixture records plus a tighter QA loop,
              not from asking the model to guess more.
            </p>
          </div>

          <dl className="mt-12 grid gap-px overflow-hidden rounded-3xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
            {results.map((result) => (
              <div key={result.label} className="bg-background p-6 sm:p-8">
                <dt className="text-5xl font-light tracking-tight">
                  {result.value}
                </dt>
                <dd className="mt-3 text-sm leading-6 text-muted-foreground">
                  {result.label}
                </dd>
              </div>
            ))}
          </dl>

          <figure className="mt-12 overflow-hidden rounded-3xl border border-border bg-white">
            <img
              src="/images/projects/trace-bim/final-tear-sheet.png"
              alt="Final Revit tear sheet with an apartment axonometric, color-coded plan, and area schedule"
              loading="lazy"
              className="mx-auto max-h-[70rem] w-full object-contain"
            />
            <figcaption className="border-t border-border bg-background px-5 py-4 text-xs leading-5 text-muted-foreground">
              Final Revit output: native model geometry, room areas, a schedule,
              and a coordinated presentation sheet.
            </figcaption>
          </figure>
        </div>
      </section>

      <section
        aria-labelledby="limits-heading"
        className="border-t border-border px-5 py-20 sm:px-8 md:py-28 lg:px-12"
      >
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.7fr_1.3fr] lg:gap-20">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Limits
            </p>
            <h2
              id="limits-heading"
              className="mt-4 text-4xl font-light tracking-tight sm:text-5xl"
            >
              What this study does not prove
            </h2>
          </div>
          <ol className="grid gap-4 sm:grid-cols-2">
            {limitations.map((limitation, index) => (
              <li
                key={limitation}
                className="rounded-3xl border border-border p-6"
              >
                <span className="font-mono text-xs text-muted-foreground">
                  0{index + 1}
                </span>
                <p className="mt-4 text-sm leading-6 text-muted-foreground">
                  {limitation}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <footer className="border-t border-neutral-800 bg-neutral-950 px-5 py-20 text-neutral-50 sm:px-8 md:py-28 lg:px-12">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-10 lg:flex-row lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-neutral-400">
              Trace-BIM
            </p>
            <h2 className="mt-4 max-w-3xl text-4xl font-light tracking-tight sm:text-5xl">
              Read the skill, schema, placement rules, and validator.
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={GITHUB_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-11 items-center justify-center rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-neutral-950 transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950"
            >
              GitHub repository{" "}
              <span aria-hidden="true" className="ml-2">
                ↗
              </span>
            </a>
            <a
              href={REPORT_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-11 items-center justify-center rounded-full border border-neutral-700 px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950"
            >
              Full case-study PDF{" "}
              <span aria-hidden="true" className="ml-2">
                ↗
              </span>
            </a>
          </div>
        </div>
      </footer>
    </main>
  );
}
